import { useCallback, useRef } from "react"
import { useLazyQuery } from "@apollo/client"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useMonitoringActivationWorkflow } from "@hydrologiq/shared/chakra/MonitoringActivationWorkflow"
import {
  SystemAssetMonitoringActivateDocument,
  SystemAssetMonitoringDeactivateDocument,
  GetInstallationAssetDataDocument,
} from "@hydrologiq/gql/graphql"
import { GetSystemViewDocument } from "@hydrologiq/gql-experience/graphql"

export interface UseSystemsMonitoringWorkflowProps {
  systemId?: string
  instanceId?: string
  assetId?: string
  assignmentId?: string
  installationId?: string

  // Optional conflict detection
  checkGroupConflict?: (installationId: string, currentAssetId: string) => boolean

  // Callbacks
  onSuccess?: () => void
  onError?: (error: any) => void
  onRefresh?: () => void | Promise<void>

  // Configuration
  entityName?: string
  confirmActivation?: boolean // Whether to show confirmation dialog for activation

  // Portal/positioning
  container?: React.RefObject<HTMLElement | null> // Container for dialog portal

  // Systems-specific polling configuration
  pollingTimeoutMs?: number
  pollingIntervalMs?: number
}

export interface UseSystemsMonitoringWorkflowReturn {
  // Workflow state
  isOpen: boolean
  action: "activate" | "deactivate" | null

  // Actions
  openActivation: () => void
  openDeactivation: () => void
  close: () => void

  // Handlers for MonitoringActivationWorkflow
  handlePreActivate: () => Promise<boolean | string>
  handleActivate: () => Promise<void>
  handleDeactivate: () => Promise<void>
  handleConflict: (message: string) => void
  handleRefresh: () => void | Promise<void>

  // Loading states
  isActivating: boolean
  isDeactivating: boolean

  // Workflow component props
  workflowProps: {
    confirmActivation: boolean
    container?: React.RefObject<HTMLElement | null>
    entityName: string
  }
}

export const useSystemsMonitoringWorkflow = ({
  systemId,
  instanceId,
  assetId,
  assignmentId,
  installationId,
  checkGroupConflict,
  onSuccess,
  onError,
  onRefresh,
  entityName = "monitoring",
  confirmActivation = true, // Default to true for systems workflow
  container,
  pollingTimeoutMs = 4 * 60 * 1000, // 4 minutes default
  pollingIntervalMs = 20 * 1000, // 20 seconds default
}: UseSystemsMonitoringWorkflowProps): UseSystemsMonitoringWorkflowReturn => {
  // Use the base workflow hook
  const baseWorkflow = useMonitoringActivationWorkflow()

  // GraphQL mutations for systems
  const [activateSystemMonitoring, { loading: isActivating }] = useMutationWithEndpoint(
    SystemAssetMonitoringActivateDocument,
    {
      refetchQueries: [GetSystemViewDocument],
    },
    "primary"
  )
  const [deactivateSystemMonitoring, { loading: isDeactivating }] = useMutationWithEndpoint(
    SystemAssetMonitoringDeactivateDocument,
    {
      refetchQueries: [GetSystemViewDocument],
    },
    "primary"
  )

  // Polling-related refs and queries
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isPollingActiveRef = useRef<boolean>(false)

  const [checkTimeSeriesData] = useLazyQuery(GetInstallationAssetDataDocument, {
    errorPolicy: "all",
    notifyOnNetworkStatusChange: false,
    onError: () => {}, // Silently handle errors during polling
  })

  // Pre-activation conflict detection
  const handlePreActivate = useCallback(async (): Promise<boolean | string> => {
    // Optional conflict detection - only runs if checkGroupConflict is provided
    if (checkGroupConflict && installationId && assetId) {
      const hasConflict = checkGroupConflict(installationId, assetId)
      if (hasConflict) {
        return "Only one asset can be monitored at a time within this group. Deactivate monitoring for the other asset first."
      }
    }
    return true // No conflict
  }, [checkGroupConflict, installationId, assetId])

  // Cleanup function for polling timers
  const cleanupPolling = useCallback(() => {
    isPollingActiveRef.current = false
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
  }, [])

  // Handle activation using systems-specific mutation with polling validation
  const handleActivate = useCallback(
    async (volume?: number | null, min_pressure?: number | null): Promise<void> => {
      if (!systemId || !instanceId || !assetId || !assignmentId) {
        throw new Error("Missing required parameters for systems monitoring activation")
      }

      // Execute the mutation first
      await activateSystemMonitoring({
        variables: {
          systemId,
          instanceId,
          assetId,
          assignmentId,
          volume: volume ?? undefined,
          minPressure: min_pressure ?? undefined,
        },
      })

      // For systems component, we can add polling validation here if needed
      // This is where the time series polling logic would be integrated
      // For now, we'll rely on the standard workflow success handling
    },
    [systemId, instanceId, assetId, assignmentId, activateSystemMonitoring]
  )

  // Handle deactivation using systems-specific mutation
  const handleDeactivate = useCallback(
    async (volume?: number | null, min_pressure?: number | null): Promise<void> => {
      if (!systemId || !instanceId || !assetId || !assignmentId) {
        throw new Error("Missing required parameters for systems monitoring deactivation")
      }

      await deactivateSystemMonitoring({
        variables: {
          systemId,
          instanceId,
          assetId,
          assignmentId,
          volume: volume ?? undefined,
          minPressure: min_pressure ?? undefined,
        },
      })
    },
    [systemId, instanceId, assetId, assignmentId, deactivateSystemMonitoring]
  )

  // Handle conflict - can be customized for systems-specific behavior
  const handleConflict = useCallback((message: string) => {
    console.warn(`Systems monitoring conflict: ${message}`)
    // Could be extended to show custom conflict UI, logging, etc.
  }, [])

  // Handle refresh - use provided onRefresh or no-op
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      return onRefresh()
    }
  }, [onRefresh])

  return {
    // Workflow state from base hook
    isOpen: baseWorkflow.isOpen,
    action: baseWorkflow.action,

    // Actions from base hook
    openActivation: baseWorkflow.openActivation,
    openDeactivation: baseWorkflow.openDeactivation,
    close: baseWorkflow.close,

    // Systems-specific handlers
    handlePreActivate,
    handleActivate,
    handleDeactivate,
    handleConflict,
    handleRefresh,

    // Loading states
    isActivating,
    isDeactivating,

    // Workflow component props
    workflowProps: {
      confirmActivation,
      container,
      entityName,
    },
  }
}
